/**
 * Manages the behavior and layout of product facets/filters on a product listing page (PLP), adjusting their display based on screen size and user interactions.
 *
 * Key Features:
 * 1. Responsive Layout: Dynamically adjusts the placement of facets based on the screen size, ensuring optimal usability across different devices and viewport sizes.
 * 2. Media Query Handling: Listens for changes in media queries and repositions facets accordingly, maintaining consistency and responsiveness in the UI layout.
 * 3. Facet Interaction Handling: Handles user interactions with facets, including opening/closing facets on mobile devices and applying facet selections.
 * 4. Facet State Management: Tracks the state of selected facets and updates the parent context with the chosen filters, facilitating accurate product filtering and search.
 * 5. Special Handling: Provides special handling for specific facet interactions, such as toggling special filters or clearing all selected facets.
 *
 * Usage:
 * This component is integrated into the product listing page (PLP) to control the behavior and appearance of product facets. It enhances the filtering functionality, making it easier for users to refine their product search based on various criteria.
 */

const productsBrowserFacetsWrapper = () => ({
  isSpecialOpen: false,
  filterCollectionsCache: {},

  init() {
    this.dialog = this.$el.querySelector('dialog');
    this.append();
    this.handleMediaQuery = this.handleMediaQuery.bind(this);
    matcher.addEventListener('change', this.handleMediaQuery);
  },

  destroy() {
    matcher.removeEventListener('change', this.handleMediaQuery);
  },

  append() {
    // hack to render only once in dom but move around based on media query

    if (matcher.matches) {
      this.dialog.close();
      this.$refs.sidebar.appendChild(this.$refs.facets);
    } else {
      this.$el.querySelector('.js-products-browser-facets-wrapper-drawer').appendChild(this.$refs.facets);
    }
  },

  handleMediaQuery() {
    this.append();
  },

  handleMobileOpen() {
    this.dialog.showModal();
  },

  handleDrawerOpen() {
    this.ignoreUpdates = true; // in parent context
  },

  handleDrawerClose() {
    this.ignoreUpdates = false; // in parent context
  },

  async applyFacets() {
    // updates things in parent context
    const newActiveFacets = this.activeFacets;
    const newSort = this.sort;
    this.ignoreUpdates = false;
    await this.$nextTick();
    this.activeFacets = newActiveFacets;
    this.sort = newSort;
    this.queryProducts();
    this.close();
  },

  async clearAllFacets() {
    // updates activeFacets in parent context
    this.activeFacets = this.getEmptyFacets();
    await this.$nextTick();
    this.applyFacets();
  },

  handleMobileToggleSpecial() {
    this.isSpecialOpen = !this.isSpecialOpen;
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productsBrowserFacetsWrapper', productsBrowserFacetsWrapper);
});